import { useEffect, useState } from "react";
import { AlertTriangle, X, CheckCircle2 } from "lucide-react";
import { useShiftFreeze } from "@/context/ShiftFreezeContext";

const WARN_THRESHOLD_MIN = 5;

const KIND_COPY = {
  shift_end: "Your shift ends",
  break_start: "Scheduled break starts",
};

export default function ShiftFreezeWarning() {
  const { status } = useShiftFreeze();
  const [dismissedAt, setDismissedAt] = useState(null);

  const mins = status?.minutes_until_freeze;
  const kind = status?.next_freeze_kind;
  const at = status?.next_freeze_at;
  const breakLabel = status?.next_break_label;
  const ext = status?.extension;

  // Reset dismissal whenever the upcoming-freeze event changes (e.g., user
  // crossed into a new shift segment) so the new event still warns once.
  useEffect(() => {
    setDismissedAt(null);
  }, [kind, at]);

  // Hide while frozen (overlay handles it), or when no schedule, or after the
  // user dismissed this specific countdown.
  if (!status || status.frozen) return null;

  // PRIORITY 1: Extension is active → show a persistent emerald banner so the
  // user knows their shift was extended and they can continue working.
  if (ext?.active && ext?.minutes_left > 0) {
    return (
      <div
        data-testid="shift-extension-banner"
        className="fixed top-16 right-4 z-[8000] max-w-sm border border-emerald-300 bg-emerald-50 text-emerald-900 rounded-sm shadow-lg p-3 flex items-start gap-2 animate-in fade-in slide-in-from-top-2"
      >
        <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-xs leading-snug flex-1 min-w-0">
          <div className="font-medium" data-testid="extension-banner-title">
            Shift extended by 20 minutes
          </div>
          <div className="text-emerald-800/80 mt-0.5" data-testid="extension-banner-countdown">
            {ext.minutes_left === 1
              ? "1 minute remaining — wrap up and save your work."
              : `${ext.minutes_left} minutes remaining — keep working.`}
          </div>
        </div>
      </div>
    );
  }

  if (mins == null || mins > WARN_THRESHOLD_MIN || mins < 0) return null;
  if (dismissedAt && dismissedAt === `${kind}:${at}`) return null;

  const title = KIND_COPY[kind] || "App will lock";
  const inText = mins <= 0 ? "any moment" : (mins === 1 ? "1 minute" : `${mins} minutes`);

  return (
    <div
      data-testid="shift-freeze-warning"
      className="fixed top-16 right-4 z-[8000] max-w-sm border border-amber-300 bg-amber-50 text-amber-900 rounded-sm shadow-lg p-3 flex items-start gap-2 animate-in fade-in slide-in-from-top-2"
    >
      <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
      <div className="text-xs leading-snug flex-1 min-w-0">
        <div className="font-medium" data-testid="shift-warning-title">
          {title} in {inText}
        </div>
        <div className="text-amber-800/80 mt-0.5">
          {kind === "break_start"
            ? `${breakLabel || "Break"} starts at ${at}. App will lock — save your work.`
            : `App will lock at ${at}. Wrap up or request a 20-min extension once locked.`}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissedAt(`${kind}:${at}`)}
        className="text-amber-800 hover:text-amber-950 -mt-0.5 -mr-1 p-0.5"
        data-testid="shift-warning-dismiss"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
